import api from './api';

// Super Admin Dashboard API
export const superAdminDashboardApi = {
  // Get platform-wide statistics
  getStats() {
    return api.get('/super/dashboard');
  }
};

// Super Admin Company Management API
export const superAdminCompanyApi = {
  // List all companies
  list(params = {}) {
    return api.get('/super/companies', { params });
  },

  // Get company details
  get(id) {
    return api.get(`/super/companies/${id}`);
  },

  // Update company
  update(id, data) {
    return api.put(`/super/companies/${id}`, data);
  },

  // Toggle company active status
  toggle(id) {
    return api.post(`/super/companies/${id}/toggle`);
  },

  // Get company admins
  getAdmins(id) {
    return api.get(`/super/companies/${id}/admins`);
  },

  // Get company orders
  getOrders(id, params = {}) {
    return api.get(`/super/companies/${id}/orders`, { params });
  }
};

// Super Admin Subscription Management API
export const superAdminSubscriptionApi = {
  // List all subscriptions
  list(params = {}) {
    return api.get('/super/subscriptions', { params });
  },

  // Get subscription statistics
  getStats() {
    return api.get('/super/subscriptions/stats');
  },

  // List all invoices
  listInvoices(params = {}) {
    return api.get('/super/invoices', { params });
  },

  // Get invoice statistics
  getInvoiceStats() {
    return api.get('/super/invoices/stats');
  },

  // Mark invoice as paid
  markInvoicePaid(id) {
    return api.post(`/super/invoices/${id}/mark-paid`);
  }
};

// Super Admin Plan Management API
export const superAdminPlanApi = {
  // List all plans
  list() {
    return api.get('/super/plans');
  },

  // Create new plan
  create(data) {
    return api.post('/super/plans', data);
  },

  // Update plan
  update(id, data) {
    return api.put(`/super/plans/${id}`, data);
  }
};

// Super Admin Customer Management API (Platform-wide)
export const superAdminCustomerApi = {
  // List all customers
  list(params = {}) {
    return api.get('/super/customers', { params });
  },

  // Get customer details
  get(id) {
    return api.get(`/super/customers/${id}`);
  },

  // Get customer statistics
  getStats() {
    return api.get('/super/customers/stats');
  }
};

// Super Admin Promo Code Management API
export const superAdminPromoApi = {
  // List all promo codes
  list(params = {}) {
    return api.get('/super/promos', { params });
  },

  // Get promo code details
  get(id) {
    return api.get(`/super/promos/${id}`);
  },

  // Create promo code
  create(data) {
    return api.post('/super/promos', data);
  },

  // Update promo code
  update(id, data) {
    return api.put(`/super/promos/${id}`, data);
  },

  // Delete promo code
  delete(id) {
    return api.delete(`/super/promos/${id}`);
  },

  // Toggle promo code status
  toggleStatus(id) {
    return api.post(`/super/promos/${id}/toggle`);
  },

  // Check code availability
  checkCode(code, excludeId = null) {
    return api.get('/super/promos/check-code', {
      params: { code, exclude_id: excludeId }
    });
  },

  // Get promo statistics
  getStats() {
    return api.get('/super/promos/stats');
  }
};

// Super Admin Delivery Management API
export const superAdminDeliveryApi = {
  // Dashboard
  getDashboard() {
    return api.get('/super/delivery/dashboard');
  },

  // Deliveries
  getDeliveries(params = {}) {
    return api.get('/super/delivery/deliveries', { params });
  },

  getDelivery(id) {
    return api.get(`/super/delivery/deliveries/${id}`);
  },

  // Settings
  getSettings() {
    return api.get('/super/delivery/settings');
  },

  updateSettings(data) {
    return api.put('/super/delivery/settings', data);
  },

  updateLalamove(data) {
    return api.put('/super/delivery/settings/lalamove', data);
  },

  updateGrab(data) {
    return api.put('/super/delivery/settings/grab', data);
  },

  testFeeCalculation(data) {
    return api.post('/super/delivery/settings/test-fee', data);
  },

  getZones() {
    return api.get('/super/delivery/settings/zones');
  },

  updateZones(zones) {
    return api.put('/super/delivery/settings/zones', { zones });
  },

  // Riders
  getRiders(params = {}) {
    return api.get('/super/delivery/riders', { params });
  },

  getRiderStats() {
    return api.get('/super/delivery/riders/stats');
  },

  getOutlets() {
    return api.get('/super/delivery/riders/outlets');
  },

  getRider(id) {
    return api.get(`/super/delivery/riders/${id}`);
  },

  createRider(data) {
    return api.post('/super/delivery/riders', data);
  },

  updateRider(id, data) {
    return api.put(`/super/delivery/riders/${id}`, data);
  },

  deleteRider(id) {
    return api.delete(`/super/delivery/riders/${id}`);
  },

  toggleRider(id) {
    return api.post(`/super/delivery/riders/${id}/toggle`);
  },

  getRiderDeliveries(id, params = {}) {
    return api.get(`/super/delivery/riders/${id}/deliveries`, { params });
  }
};

// Super Admin Wallet Management API
// Platform-level wallet operations (GetLokal as platform owner)
export const superAdminWalletApi = {
  // Get platform-wide wallet statistics
  getStats() {
    return api.get('/super/wallet/stats');
  },

  // List all wallet transactions platform-wide
  getTransactions(params = {}) {
    return api.get('/super/wallet/transactions', { params });
  },

  // Get all customers with wallet balances
  getCustomers(params = {}) {
    return api.get('/super/wallet/customers', { params });
  },

  // Get specific customer wallet details
  getCustomerWallet(customerId) {
    return api.get(`/super/wallet/customers/${customerId}`);
  },

  // Get customer transaction history
  getCustomerTransactions(customerId, params = {}) {
    return api.get(`/super/wallet/customers/${customerId}/transactions`, { params });
  },

  // Adjust customer wallet balance (refund/compensation)
  adjustBalance(customerId, data) {
    return api.post(`/super/wallet/customers/${customerId}/adjust`, data);
  },

  // Manual top-up for customer
  manualTopup(customerId, data) {
    return api.post(`/super/wallet/customers/${customerId}/topup`, data);
  }
};

// Super Admin Loyalty Tier Management API
export const superAdminTierApi = {
  list() {
    return api.get('/super/loyalty/tiers');
  },
  get(id) {
    return api.get(`/super/loyalty/tiers/${id}`);
  },
  create(data) {
    return api.post('/super/loyalty/tiers', data);
  },
  update(id, data) {
    return api.put(`/super/loyalty/tiers/${id}`, data);
  },
  delete(id) {
    return api.delete(`/super/loyalty/tiers/${id}`);
  },
  reorder(tiers) {
    return api.post('/super/loyalty/tiers/reorder', { tiers });
  }
};

// Super Admin Platform Settings API
export const superAdminSettingsApi = {
  get(group = null) {
    const params = group ? { group } : {};
    return api.get('/super/settings', { params });
  },
  update(settings) {
    return api.put('/super/settings', { settings });
  }
};

export default {
  dashboard: superAdminDashboardApi,
  companies: superAdminCompanyApi,
  subscriptions: superAdminSubscriptionApi,
  plans: superAdminPlanApi,
  customers: superAdminCustomerApi,
  promos: superAdminPromoApi,
  delivery: superAdminDeliveryApi,
  wallet: superAdminWalletApi,
  tiers: superAdminTierApi,
  settings: superAdminSettingsApi
};
